import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { cn } from '@/lib/utils'
import type { WidgetDensity, WidgetTone } from '../types'
import { TONE_STYLES, widgetCardClass } from './tones'

type WidgetFrameProps = {
  icon: LucideIcon
  title: ReactNode
  tone: WidgetTone
  density: WidgetDensity
  badge?: ReactNode
  to?: string
  frameless?: boolean
  className?: string
  children: ReactNode
}

export function WidgetFrame({ icon: Icon, title, tone, density, badge, to, frameless, className, children }: WidgetFrameProps) {
  const { t } = useTranslation()
  const style = TONE_STYLES[tone]
  const compact = density === 'compact'

  if (frameless) {
    return <section className={cn('@container', widgetCardClass, compact ? 'p-3' : 'p-4', className)}>{children}</section>
  }

  return (
    <section className={cn(widgetCardClass, className)}>
      <header className={cn('flex items-center gap-2.5', compact ? 'px-3 pt-3 pb-2' : 'px-4 pt-4 pb-3')}>
        <span className={cn('flex shrink-0 items-center justify-center rounded-lg ring-1 ring-inset', compact ? 'size-7 [&_svg]:size-3.5' : 'size-8 [&_svg]:size-4', style.icon)}>
          <Icon aria-hidden />
        </span>
        <h3 className="min-w-0 truncate text-sm font-semibold tracking-tight">{title}</h3>
        {badge}
        {to ? (
          <Link to={to} className="ml-auto shrink-0 text-xs font-medium text-primary hover:underline">
            {t('common.seeAll')}
          </Link>
        ) : null}
      </header>
      <div className={cn('@container min-h-0 flex-1 overflow-y-auto scrollbar-none', compact ? 'px-3 pb-3' : 'px-4 pb-4')}>{children}</div>
    </section>
  )
}

export function PersonalBadge() {
  const { t } = useTranslation()
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-orange/10 px-2 py-0.5 text-[11px] font-medium text-brand-orange">
      {t('dashboard.personalOnly')}
    </span>
  )
}
